import axios from "axios";
import { createNewHmac, generateHmacHeaders } from "./hmacUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; // replace with your API base URL

let isLoggingOutAdmin = false;
let pendingApiRequests = 0;
const apiLoadingListeners = new Set();

const notifyApiLoadingListeners = () => {
  apiLoadingListeners.forEach((listener) => listener());
};

export const subscribeToApiLoading = (listener) => {
  apiLoadingListeners.add(listener);
  return () => apiLoadingListeners.delete(listener);
};

export const getPendingApiRequests = () => pendingApiRequests;

export const handleUnauthorizedAdmin = () => {
  if (isLoggingOutAdmin) return;

  const currentPath = window.location.pathname;
  if (currentPath.startsWith("/adminlogin")) {
    return;
  }

  isLoggingOutAdmin = true;

  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("id");
    localStorage.removeItem("updateLegalData");
  } catch (err) {
    console.error("Error clearing admin session:", err);
  }

  window.location.href = "/adminlogin";
};

const isLoginRequest = (url = "") => {
  if (!url) return false;
  const str = String(url).toLowerCase();
  return (
    str.includes("admin/login") ||
    str.includes("login/complete") ||
    str.endsWith("/login") ||
    str === "login"
  );
};

// Global Axios request interceptor for admin to ensure HMAC headers on every request
axios.interceptors.request.use(
  (config) => {
    // If HMAC headers already provided, skip
    if (
      config.headers?.["x-hmac-signature"] ||
      config.headers?.["x_hmac_signature"] ||
      config.headers?.["X-Hmac-Signature"]
    ) {
      return config;
    }

    let endpoint = config.url || "";
    if (API_BASE_URL && endpoint.startsWith(API_BASE_URL)) {
      endpoint = endpoint.slice(API_BASE_URL.length).replace(/^\/+/, "");
    }

    const hmacHeaders = generateHmacHeaders({
      method: config.method || "GET",
      endpoint: endpoint,
      data: config.data,
    });

    config.headers = {
      ...(config.headers || {}),
      ...hmacHeaders,
    };

    return config;
  },
  (error) => Promise.reject(error)
);

// Global Axios response interceptor for admin
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const requestUrl = error?.config?.url || "";
      const errorMsg = String(
        error?.response?.data?.msg || error?.response?.data?.message || ""
      );
      // Only logout on genuine session/auth expiry, never on HMAC mismatch
      if (
        !isLoginRequest(requestUrl) &&
        !errorMsg.toLowerCase().includes("hmac")
      ) {
        handleUnauthorizedAdmin();
      }
    }
    return Promise.reject(error);
  }
);

// API request function
export const apiRequest = async (
  endpoint,
  method = "GET",
  data = null,
  headers = {},
) => {
  const url = `${API_BASE_URL}/${endpoint}`; // Ensure the URL is constructed properly

  // Safeguard: If data only contains Authorization header and headers is empty, caller passed headers as 3rd arg
  if (
    data &&
    typeof data === "object" &&
    !(data instanceof FormData) &&
    data.Authorization &&
    Object.keys(data).length === 1 &&
    Object.keys(headers).length === 0
  ) {
    headers = data;
    data = null;
  }

  // Generate HMAC security headers
  const hmacHeaders = generateHmacHeaders({
    method,
    endpoint,
    data,
  });

  const options = {
    method: method,
    headers: {
      ...(data instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...headers,
      ...hmacHeaders,
    },
  };

  if (data !== null && data !== undefined) {
    options.data = data;
  }

  pendingApiRequests += 1;
  notifyApiLoadingListeners();

  try {
    const response = await axios(url, options);

    if (!response) {
      throw new Error(`Error: ${response.status}`);
    }

    return response;
  } catch (error) {
    if (error?.response?.status === 401) {
      const errorMsg = String(
        error?.response?.data?.msg || error?.response?.data?.message || ""
      );
      if (
        !isLoginRequest(endpoint) &&
        !isLoginRequest(url) &&
        !errorMsg.toLowerCase().includes("hmac")
      ) {
        handleUnauthorizedAdmin();
      }
    }
    console.error("API request failed:", error);
    throw error;
  } finally {
    pendingApiRequests = Math.max(0, pendingApiRequests - 1);
    notifyApiLoadingListeners();
  }
};
